import { useMemo, useState } from "react";
import { IconChevronDown, IconPencil, IconSearch } from "@tabler/icons-react";
import {
  Badge,
  BotonExportarExcel,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Group,
  Input,
  Paper,
  Stack,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@/components/ui";
import { cn } from "@/lib/cn";
import type { FilaAreaNiveles, GrupoTipoSubtipo } from "../utils/agrupar-niveles";
import type { NivelAutorizacion } from "../services/niveles-service";
import { useEstablecerGeneraPolizaEnTodasLasAreas } from "../hooks/use-niveles-mutations";
import { NivelEditarModalBody } from "./NivelEditarModalBody";

interface Editando {
  nivel: NivelAutorizacion;
  contexto: string;
}

/**
 * Vista MATRIZ de los niveles · secciones colapsables por (tipo, subtipo) y,
 * dentro, una tabla área × nivel (mucho más compacta que un card por
 * combinación). Reemplaza `<Accordion>` de Mantine (sin átomo compartido ·
 * único consumidor) con un `<details>`-like manual por sección, abierto por
 * default. Cada celda con nivel es clicable y abre el diálogo de
 * edición/eliminación.
 */
export function NivelesMatriz({ grupos }: { grupos: GrupoTipoSubtipo[] }) {
  const [filtroArea, setFiltroArea] = useState("");
  const [editando, setEditando] = useState<Editando | null>(null);

  const gruposFiltrados = useMemo(() => {
    const q = filtroArea.trim().toLowerCase();
    if (!q) return grupos;
    return grupos
      .map((g) => ({ ...g, areas: g.areas.filter((a) => a.areaNombre.toLowerCase().includes(q)) }))
      .filter((g) => g.areas.length > 0);
  }, [grupos, filtroArea]);

  return (
    <>
      <Group justify="space-between" align="flex-start" className="mb-4">
        <Input
          placeholder="Filtrar por área…"
          leftSection={<IconSearch size={14} />}
          value={filtroArea}
          onChange={(e) => setFiltroArea(e.currentTarget.value)}
          wrapperClassName="max-w-80 flex-1"
        />
        <BotonExportarExcel
          archivo="flujos-autorizacion"
          hoja="Niveles"
          filas={gruposFiltrados.flatMap((g) =>
            g.areas.flatMap((a) =>
              Array.from(a.porNumero.values()).map((n) => ({
                tipo: g.tipo,
                subtipo: g.subtipo,
                area: a.areaNombre,
                nivel: n.nivel,
                roles: n.rolesAutorizadores.join(", "),
                generaPoliza: n.generaPolizaAlAprobar ? "Sí" : "No",
              }))
            )
          )}
          columnas={[
            { header: "Tipo", value: (f) => f.tipo },
            { header: "Subtipo", value: (f) => f.subtipo },
            { header: "Área", value: (f) => f.area },
            { header: "Nivel", value: (f) => f.nivel },
            { header: "Roles autorizadores", value: (f) => f.roles },
            { header: "Póliza al aprobar", value: (f) => f.generaPoliza },
          ]}
        />
      </Group>
      <div className="flex flex-col gap-3">
        {gruposFiltrados.map((grupo) => (
          <GrupoSeccion key={grupo.key} grupo={grupo} onAbrir={setEditando} />
        ))}
      </div>

      <Dialog open={!!editando} onOpenChange={(open) => !open && setEditando(null)}>
        <DialogContent className="max-w-lg">
          {editando ? (
            <>
              <DialogHeader>
                <DialogTitle>Editar · {editando.contexto}</DialogTitle>
              </DialogHeader>
              <NivelEditarModalBody nivel={editando.nivel} contexto={editando.contexto} onCerrar={() => setEditando(null)} />
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}

/** Sección colapsable de un grupo (tipo, subtipo) · abierta por default. */
function GrupoSeccion({ grupo, onAbrir }: { grupo: GrupoTipoSubtipo; onAbrir: (e: Editando) => void }) {
  const [open, setOpen] = useState(true);
  return (
    <Paper className="overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full cursor-pointer items-center justify-between gap-3 px-4 py-3 text-left hover:bg-muted/50"
      >
        <Group gap="xs">
          <Text fw={600}>
            {grupo.tipo} <span className="text-muted-foreground">/</span> {grupo.subtipo}
          </Text>
          <Badge variant="light" color="slate">
            {grupo.areas.length} área(s)
          </Badge>
          <Badge variant="light" color="slate">
            {grupo.maxNivel} nivel(es)
          </Badge>
        </Group>
        <IconChevronDown size={16} className={cn("shrink-0 text-muted-foreground transition-transform", open && "rotate-180")} />
      </button>
      {open ? (
        <div className="border-t border-border p-4">
          <div className="overflow-x-auto">
            <Table verticalSpacing="xs" highlightOnHover>
              <Thead>
                <Tr>
                  <Th style={{ minWidth: 160 }}>Área</Th>
                  {Array.from({ length: grupo.maxNivel }, (_, i) => (
                    <Th key={i + 1} style={{ minWidth: 180 }}>
                      <NivelHeaderCell grupo={grupo} nivel={i + 1} />
                    </Th>
                  ))}
                </Tr>
              </Thead>
              <Tbody>
                {grupo.areas.map((fila: FilaAreaNiveles) => (
                  <Tr key={fila.areaId}>
                    <Td>
                      <Text size="sm" fw={500}>
                        {fila.areaNombre}
                      </Text>
                    </Td>
                    {Array.from({ length: grupo.maxNivel }, (_, i) => (
                      <Td key={i + 1}>
                        <Celda
                          nivel={fila.porNumero.get(i + 1) ?? null}
                          contexto={`${grupo.tipo} / ${grupo.subtipo} · ${fila.areaNombre}`}
                          onAbrir={onAbrir}
                        />
                      </Td>
                    ))}
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </div>
        </div>
      ) : null}
    </Paper>
  );
}

/**
 * Header de una columna de nivel · muestra "Nivel N" + un switch que activa/desactiva
 * la generación de póliza al aprobar ESE nivel en TODAS las áreas del flujo de una vez.
 * El switch refleja el estado agregado (todas on → on · algunas → "parcial", off).
 */
function NivelHeaderCell({ grupo, nivel }: { grupo: GrupoTipoSubtipo; nivel: number }) {
  const aplicar = useEstablecerGeneraPolizaEnTodasLasAreas();
  const niveles = grupo.areas
    .map((a) => a.porNumero.get(nivel))
    .filter((n): n is NivelAutorizacion => !!n);
  const total = niveles.length;
  const conFlag = niveles.filter((n) => n.generaPolizaAlAprobar).length;
  const todas = total > 0 && conFlag === total;
  const parcial = conFlag > 0 && conFlag < total;

  return (
    <Stack gap={4}>
      <Text size="sm" fw={600}>
        Nivel {nivel}
      </Text>
      {total > 0 ? (
        <Group gap={6} className="items-center" wrap="nowrap">
          <Switch
            checked={todas}
            disabled={aplicar.isPending}
            onCheckedChange={(v) =>
              aplicar.mutate({
                tipo: grupo.tipo,
                subtipo: grupo.subtipo,
                nivel,
                generaPolizaAlAprobar: Boolean(v),
              })
            }
            aria-label={`Generar póliza al aprobar el nivel ${nivel} en todas las áreas`}
          />
          <Text size="xs" c="dimmed" className="font-normal normal-case">
            Póliza al aprobar{parcial ? ` · parcial (${conFlag}/${total})` : ""}
          </Text>
        </Group>
      ) : null}
    </Stack>
  );
}

/** Celda de la matriz · roles del nivel (clicable → editar) o "—" si no existe. */
function Celda({
  nivel,
  contexto,
  onAbrir,
}: {
  nivel: NivelAutorizacion | null;
  contexto: string;
  onAbrir: (e: Editando) => void;
}) {
  if (!nivel) {
    return (
      <Text size="sm" c="dimmed" aria-label="Sin nivel">
        —
      </Text>
    );
  }

  return (
    <button
      type="button"
      onClick={() => onAbrir({ nivel, contexto })}
      title="Editar roles / eliminar nivel"
      className="block w-full cursor-pointer rounded-md px-1 py-0.5 text-left hover:bg-muted/60"
    >
      <Group gap={4} className="flex-wrap">
        {nivel.rolesAutorizadores.map((r) => (
          <Badge key={r} variant="light" color="info" size="sm">
            {r}
          </Badge>
        ))}
        {nivel.generaPolizaAlAprobar ? (
          <Badge variant="light" color="multiva" size="sm" title="La póliza TXT se genera al aprobar este nivel">
            Póliza al aprobar
          </Badge>
        ) : null}
        <IconPencil size={12} stroke={1.6} className="opacity-45" />
      </Group>
    </button>
  );
}
